import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Button, Card, message, Modal } from "antd";
import moment from "moment";

import style from "./style.module.scss";
import { useHistory } from "react-router-dom";
import Countdown from "components/Countdown/countdown";
import ExamNotice from "../ExamNotice";
import useModalHandler from "Utils/useModalHandler";
import useModalHandler2 from "Utils/v2/useModalHandler";
import WebCamModal from "../../../components/WebCamModal";
import { errorMessage } from "Utils/useRequest";
import {
    ExamItem,
    ExamTimeType,
    faceCheck,
    getExamSettings,
    submitPaper,
    uploadToQiNiu
} from "API";
import { isTrue } from "Utils/filter";
import useRequestFn from "../../../../../Utils/useRequestFn";
import ExamUploadModal from "../ExamUploadModal";
import ExamUploadModalPC from "../../../ExamMain/platforms/PC/components/ExamUploadModal";
import { useToggle } from "react-use";
import { phoneClickUpload } from "API";
import { ResultMessage } from "components/ResultMessage";

interface ExamBlockType {
    refreshData?: () => void;
    title: string;
    name: string;
    startDateTime: string;
    endDateTime: string;
    now: string;
    type: string;
    examId: string;
    times?: string;
    className?: string;
    platform?: "PC" | "Mobile";
    examInfo: ExamItem;
}

const ExamBlock: React.FC<ExamBlockType> = ({
    refreshData,
    name,
    title,
    startDateTime,
    endDateTime,
    now,
    examId,
    type,
    times,
    className,
    platform,
    examInfo
}) => {
    const history = useHistory();

    const [Now, setNow] = useState(now);

    const noticeModal = useModalHandler();
    const captureModal = useModalHandler2(undefined, { remove: true });
    const webcamModal = useModalHandler();
    const [doGetExamSettings, { loading }] = useRequestFn(getExamSettings);

    useEffect(() => {
        setNow(now);
    }, [now]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setNow(moment(Now).add(1, "second").format("YYYY-MM-DD HH:mm:ss"));
        }, 1000);
        return function cleanup() {
            clearTimeout(timer);
        };
    }, [Now]);

    // 截止时间
    const examEndTime = useMemo(() => {
        const format = "YYYY-MM-DD HH:mm:ss";
        if (examInfo.timeType !== ExamTimeType.Free) return endDateTime; // 非自主时间考试, 统一按截止日期算
        if (examInfo.stuStartTime)
            return moment(examInfo.stuStartTime)
                .add(examInfo.duration, "minutes")
                .format(format); // 已开始的自主考试, 截止日期为开始考试时间+考试时间
        if (moment(Now).isAfter(examInfo.endTime)) return examInfo.endTime; // 未开始,且已超过截止期限的自主考试, 返回截止期限
        return moment(Now).add(examInfo.duration, "minutes").format(format); // 未开始的自主考试, 截止日期为当前时间+考试时间
    }, [
        Now,
        endDateTime,
        examInfo.duration,
        examInfo.endTime,
        examInfo.stuStartTime,
        examInfo.timeType
    ]);

    const isBetween = useMemo(() => {
        return moment(Now).isBetween(
            startDateTime,
            examEndTime,
            undefined,
            "(]"
        );
    }, [examEndTime, Now, startDateTime]);

    const isBefore = useMemo(() => {
        return moment(Now).isSameOrBefore(startDateTime);
    }, [Now, startDateTime]);

    const isAfter = useMemo(() => {
        return moment(Now).isAfter(examEndTime);
    }, [examEndTime, Now]);

    const [disabled, setDisabled] = useState(!isBetween);

    useEffect(() => {
        setDisabled(!isBetween);
    }, [isBetween]);

    // 只允许在pc端进行的考试
    const disabledOnMobile = useMemo(() => {
        if (platform === "PC") return false;
        return isTrue(examInfo.catchPhoto) || isTrue(examInfo.realtimeVideo);
    }, [examInfo.catchPhoto, examInfo.realtimeVideo, platform]);

    const handleOral = () => {
        window.open(
            `/student/goToPlay?channelId=${examId}&userId=${localStorage.getItem(
                "user"
            )}`
        );
    };

    // 保存人脸识别信息
    const onWebCamOk = async (file: File) => {
        try {
            const data = await uploadToQiNiu(file); //获取上传后七牛地址
            await faceCheck(examId, data.url);
            webcamModal.close();
            noticeModal.open();
        } catch (e) {
            Modal.error({ content: errorMessage(e) });
        }
    };

    // 点击开始考试
    const onStartExam = async () => {
        try {
            const settings = await doGetExamSettings(examId);

            // 已经开始考试
            if (settings.stuStartTime) {
                return gotoExam();
            }

            if (isTrue(settings.catchPhoto)) return webcamModal.open();

            if (isTrue(settings.realtimeVideo)) return noticeModal.open();

            gotoExam();
        } catch (e) {
            message.error(errorMessage(e));
        }
    };

    // 跳转到考试页面
    const gotoExam = () => {
        history.push(`/exam/${examId}`);
    };

    // 只在pc上考试的提示信息
    function renderPCWarning() {
        const arr: string[] = [];

        if (isTrue(examInfo.catchPhoto)) {
            arr.push("摄像头抓拍");
        }

        if (isTrue(examInfo.realtimeVideo)) {
            arr.push("实时监控");
        }

        if (arr.length === 0) return null;

        return (
            <div className={style.warning}>
                该考核开启了{arr.join("、")}，请在pc 端完成考试
            </div>
        );
    }

    // 显示自主时间
    function renderFreeTime() {
        const timeFormat = "MM-DD HH:mm";

        return (
            <>
                <p className={style.date}>
                    有效期：{moment(startDateTime).format(timeFormat)} -{" "}
                    {moment(endDateTime).format(timeFormat)}
                </p>
                <p className={style.date}>考试时限：{examInfo.duration}分钟</p>
                <p className={style.tip}>
                    温馨提示：本考核的开考时间，考生们在有效期限内可自主安排考试时间，但考试时限不能超过本考核的考试时限，在有效期之后就不能再参加考试了。
                </p>
            </>
        );
    }

    // 显示其他考试时间
    function renderExamTime() {
        return (
            <>
                {type !== "2" && (
                    <p className={style.date}>
                        日期：{moment(startDateTime).format("MM-DD")}
                    </p>
                )}
                {type === "2" && (
                    <p className={style.date}>
                        日期：{moment(startDateTime).format("MM-DD HH:mm")} -{" "}
                        {moment(endDateTime).format("MM-DD HH:mm")}
                    </p>
                )}
                {type !== "2" && (
                    <p className={style.time}>
                        时间：{moment(startDateTime).format("HH:mm:ss")} -{" "}
                        {moment(endDateTime).format("HH:mm:ss")}
                    </p>
                )}
            </>
        );
    }

    const [visible, toggleVisible] = useToggle(false);

    const onUploadOk = () => {
        refreshData?.();
        toggleVisible();
    };

    const handleUpload = async () => {
        toggleVisible();
        try {
            await phoneClickUpload(examId);
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    };

    const handleUploadPC = () => {
        captureModal.open();
    };

    // 提交试卷
    const onSubmit = (count: number) => {
        Modal.confirm({
            title: "确定交卷？",
            content: (
                <div>
                    当前试卷中您共上传<span> {count} </span>
                    个文件，确认提交后不可修改作答内容，确认要交卷？
                </div>
            ),
            className: style.modal1,
            okText: "提交",
            cancelText: "继续做答",
            onOk: async () => {
                try {
                    await submitPaper(examId, examInfo.examInfo, "");
                    Modal.success({
                        title: "提交成功，点击确定返回...",
                        okText: "确定",
                        onOk() {
                            onUploadOk();
                        }
                    });
                } catch (e) {
                    ResultMessage.error(errorMessage(e));
                }
            }
        });
    };

    const ExamUploadModalCp = useMemo(() => {
        if (!visible) return <div />;
        return (
            <ExamUploadModal
                visible={visible}
                onCancel={toggleVisible}
                onUploadOk={onUploadOk}
                onSubmit={onSubmit}
                examId={examId}
                isAfter={isAfter}
            />
        );
    }, [visible, isAfter]);

    return (
        <Card
            className={`${className} ${
                platform === "PC" ? style.pc : style.mobile
            }`}>
            {type !== "2" && parseInt(times || "0") > 0 && (
                <div className={style.done}>已交卷</div>
            )}

            <div className={style.main}>
                <div
                    className={`${style.examDetail} ${
                        type === "1" && style.examDetailType1
                    } ${type === "2" && style.examDetailType2}`}>
                    <div className={style.examInfo}>
                        <h1 className={style.title}>{name}</h1>
                        <p className={style.date}>课程：{title}</p>

                        {examInfo.timeType === ExamTimeType.Free
                            ? renderFreeTime()
                            : renderExamTime()}
                    </div>
                    {type === "1" && (
                        <Avatar
                            className={`${style.color1} ${style.avatar}`}
                            size={60}>
                            线上考试
                        </Avatar>
                    )}
                    {type === "2" && (
                        <Avatar
                            className={`${style.color2} ${style.avatar}`}
                            size={60}>
                            论文考核
                        </Avatar>
                    )}
                    {type === "3" && (
                        <Avatar
                            className={`${style.color3} ${style.avatar}`}
                            size={60}>
                            口试
                        </Avatar>
                    )}

                    {renderPCWarning()}
                </div>

                <div className={style.footer}>
                    {!(platform === "Mobile" && type === "3") && isBefore && (
                        <p className={style.countDown}>
                            考试倒计时：
                            <Countdown
                                now={Now}
                                endDateTime={startDateTime}
                                onComplete={() => setDisabled(false)}
                            />
                        </p>
                    )}

                    <div className={style.button}>
                        {type === "1" && (
                            <Button
                                className={style.green}
                                disabled={
                                    parseInt(times || "0") > 0 ||
                                    disabled ||
                                    disabledOnMobile
                                }
                                onClick={onStartExam}
                                size="large"
                                type="default"
                                loading={loading}>
                                参加考试
                            </Button>
                        )}

                        {type === "2" && (
                            <Button
                                disabled={disabled}
                                onClick={gotoExam}
                                size="large"
                                type="primary">
                                参加考试
                            </Button>
                        )}

                        {type === "3" && (
                            <Button
                                disabled={disabled || platform === "Mobile"}
                                onClick={handleOral}
                                size="large"
                                type="danger">
                                参加考试
                            </Button>
                        )}

                        {(isTrue(examInfo.catchPhoto) ||
                            isTrue(examInfo.realtimeVideo)) &&
                            isTrue(examInfo.allowPhoneUpload) &&
                            platform === "Mobile" && (
                                <Button
                                    disabled={
                                        disabled ||
                                        isAfter ||
                                        parseInt(times || "0") > 0
                                    }
                                    size="large"
                                    type="primary"
                                    ghost
                                    onClick={handleUpload}>
                                    上传作答内容
                                    {(isAfter ||
                                        parseInt(times || "0") > 0) && (
                                        <span>（{examInfo.fileNum || 0}）</span>
                                    )}
                                </Button>
                            )}
                    </div>
                    {type === "3" && (
                        <p className={style.notification}>
                            注意：口试只支持电脑端
                        </p>
                    )}
                </div>

                {!(platform === "Mobile" && type === "3") && isBetween && (
                    <p className={style.countDown}>
                        距离截止时间还有：
                        <Countdown
                            now={Now}
                            endDateTime={examEndTime}
                            onComplete={() => setDisabled(true)}
                        />
                    </p>
                )}

                {!(platform === "Mobile" && type === "3") && isAfter && (
                    <p className={style.countDown}>
                        <span>考试已结束</span>
                    </p>
                )}

                {examInfo.fileNum !== null && (
                    <p className={style.fileNum}>
                        上传附件数：{examInfo.fileNum || 0}
                    </p>
                )}

                {!!examInfo.meetingRoom && (
                    <div className={style.meetingRoom}>
                        {examInfo.meetingRoom}
                    </div>
                )}
            </div>

            <ExamNotice visible={noticeModal.visible} onOk={gotoExam} />
            <WebCamModal
                visible={webcamModal.visible}
                onOk={onWebCamOk}
                onCancel={webcamModal.close}
            />

            {captureModal.render(
                <ExamUploadModalPC examId={examId} onSubmit={onSubmit} />
            )}

            {ExamUploadModalCp}
        </Card>
    );
};

export default ExamBlock;
